import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import axiosInstance from 'shared/services/api/axiosInstance';
import Icon from 'shared/components/Icon';
import FloatingMenu from 'shared/components/ui/FloatingMenu';
import { activityToolbarButtonClass } from 'features/activity/styles/dataTableStyles';

/**
 * "All agents ▾": pick which agents a list shows. Empty selection means all.
 * The agent list is fetched the first time the menu opens, not on page load.
 */
export default function AgentMultiSelect({ teamId, value = [], onChange, agents: knownAgents = null }) {
  const [open, setOpen] = useState(false);
  const [fetched, setFetched] = useState(null);
  // A page that already has the agent list passes it in; otherwise fetch on first open.
  const agents = knownAgents || fetched;
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open || agents || !teamId) return undefined;
    const ac = new AbortController();
    axiosInstance
      .get('/agent-runs/agents', { params: { team_id: teamId }, signal: ac.signal })
      .then((res) => setFetched((res.data?.agents || []).map((a) => a.agent_name).filter(Boolean)))
      .catch(() => { if (!ac.signal.aborted) setFetched([]); });
    return () => ac.abort();
  }, [open, agents, teamId]);

  const close = useCallback(() => setOpen(false), []);

  const toggle = (name) => onChange(value.includes(name) ? value.filter((v) => v !== name) : [...value, name]);
  const label = value.length === 0 ? 'All agents' : value.length === 1 ? value[0] : `${value.length} agents`;

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className={activityToolbarButtonClass}
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        <Icon name="agent" size={14} color="var(--text-muted)" />
        <span className="max-w-[160px] truncate">{label}</span>
        <ChevronDown size={13} className="text-[color:var(--text-muted)]" />
      </button>
      <FloatingMenu anchorRef={rootRef} open={open} onClose={close} width={240} scroll={false} maxHeight={320}
        role="listbox" aria-multiselectable="true" aria-label="Agents" className="p-1.5">
        <Option label="All agents" selected={value.length === 0} onClick={() => onChange([])} />
        {agents == null ? (
          <div className="px-2.5 py-2 text-[12px] text-[color:var(--text-muted)]">Loading agents…</div>
        ) : agents.length === 0 ? (
          <div className="px-2.5 py-2 text-[12px] text-[color:var(--text-muted)]">No agents yet.</div>
        ) : (
          <div className="mt-1 min-h-0 flex-1 overflow-y-auto border-t border-[color:var(--border-default)] pt-1">
            {agents.map((name) => (
              <Option key={name} label={name} selected={value.includes(name)} onClick={() => toggle(name)} />
            ))}
          </div>
        )}
      </FloatingMenu>
    </div>
  );
}

function Option({ label, selected, onClick }) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      onClick={onClick}
      className="flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-1.5 text-left text-[13px] text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover"
    >
      <span className="truncate">{label}</span>
      <Check size={14} strokeWidth={2.5} className="shrink-0" style={{ opacity: selected ? 1 : 0 }} aria-hidden />
    </button>
  );
}
